import { FileExcelOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Flex, message, Skeleton, Typography } from 'antd'
import { TooltipParagraph } from 'features/drilldown/components/TooltipParagraph'
import { DrilldownDataContext } from 'features/drilldown/data/savedExplorationState/DrilldownExplorationContext'
import { useNiiPdfExportScope } from 'features/drilldown/panels/impl/niiPdfExport/useNiiPdfExportScope'
import {
  isNiiSheetIncluded,
  NII_SHEET_KEYS,
  NII_SHEET_LABELS,
  selectedNiiSheetColumns,
} from 'features/libra/stepComponents/niiReport/sheet/niiSheetSelection'
import { useBulkNiiSheetExport } from 'features/libra/stepComponents/niiReport/sheet/useBulkNiiSheetExport'
import { type FC, type ReactNode, useContext } from 'react'
import styled from 'styled-components'

import { useNiiReportSheetSelection } from './useNiiReportSheetSelection'

const { Text } = Typography

export const NII_REPORT_SHEET_EXPORT_TITLE_TOOLTIP: ReactNode = (
  <>
    <TooltipParagraph>
      Downloads one spreadsheet covering every asset in the current scope
      &mdash; the latest <b>complete</b> analysis by default, or the latest
      version of every ticket when in-progress tickets are included in the panel
      configuration.
    </TooltipParagraph>
    <TooltipParagraph>
      Three sheets, joined by ticket: <b>Tickets</b> (one row per asset),{' '}
      <b>Zones</b> (one row per zone) and <b>Inspection History</b>.
    </TooltipParagraph>
    <TooltipParagraph>
      Narrow it with a filter panel above to export a subset.
    </TooltipParagraph>
  </>
)

const Container = styled(Flex)`
  padding: 1rem;
  height: 100%;
  overflow-y: auto;
`

const TicketList = styled.ul`
  margin: 0;
  padding-left: 1.25rem;
  max-height: 12rem;
  overflow-y: auto;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;

  li {
    margin-bottom: 0.125rem;
  }
`

/**
 * Bulk NII report export to a spreadsheet for the current drilldown scope.
 *
 * Shares `useNiiPdfExportScope` with the PDF panel, so by default both
 * exports cover exactly the same tickets. Unlike the PDF panel this produces
 * a single file however large the scope, so there is no large-export warning.
 *
 * Which sheets and columns land in the file, and whether in-progress tickets
 * are included, is chosen in the config drawer
 * (`NiiReportSheetExportConfigView`) and persisted in panel data.
 */
export const NiiReportSheetExportPanel: FC = () => {
  const { orgId } = useContext(DrilldownDataContext)
  const { selection, includeIncomplete } = useNiiReportSheetSelection()
  const { tickets, workflowMap, ineligibleCount, isLoading } =
    useNiiPdfExportScope({ includeIncomplete })
  const [messageApi, contextHolder] = message.useMessage()

  const {
    generateAndDownload,
    isGenerating,
    isLoading: signoffsLoading,
    columnsBySheet,
    missingIdentity,
  } = useBulkNiiSheetExport(tickets, workflowMap, orgId ?? '', selection)

  const includedSheets = NII_SHEET_KEYS.filter((sheet) =>
    isNiiSheetIncluded(sheet, selection)
  )

  const isColumnExported = (column: string): boolean =>
    includedSheets.some((sheet) =>
      selectedNiiSheetColumns(sheet, columnsBySheet[sheet], selection).includes(
        column
      )
    )

  // Only warn about a column the export actually carries — a column the user
  // deselected is a choice, not a gap.
  const identityWarnings = [
    { column: 'Asset ID', titles: missingIdentity.assetId },
    { column: 'Site', titles: missingIdentity.site },
  ]
    .filter(
      ({ column, titles }) => titles.length > 0 && isColumnExported(column)
    )
    .map(({ column, titles }) => `${column} — ${titles.join(', ')}`)

  const handleExport = async (): Promise<void> => {
    try {
      const { exportedCount, skipped } = await generateAndDownload()

      if (exportedCount === 0) {
        messageApi.error('Nothing to export — no NII report content found.')
      } else if (skipped.length > 0) {
        messageApi.warning(
          `${exportedCount} asset${exportedCount > 1 ? 's' : ''} exported. Skipped: ${skipped
            .map((ticket) => ticket.title)
            .join(', ')}`
        )
      } else {
        messageApi.success(
          `Spreadsheet with ${exportedCount} asset${exportedCount > 1 ? 's' : ''} saved to your downloads folder`
        )
      }
    } catch (error) {
      console.error('Error exporting NII report spreadsheet:', error)
      messageApi.error('Failed to export the spreadsheet. Please try again.')
    }
  }

  if (isLoading) return <Skeleton active paragraph={{ rows: 3 }} />

  if (tickets.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          ineligibleCount > 0
            ? `No exportable NII reports in scope — ${ineligibleCount} ticket${ineligibleCount > 1 ? 's have' : ' has'} no NII Report step`
            : `No ${includeIncomplete ? '' : 'completed '}NII tickets in the current scope`
        }
      />
    )
  }

  return (
    <Container vertical gap='small'>
      {contextHolder}
      <Text>
        <b>{tickets.length}</b> NII report{tickets.length > 1 ? 's' : ''} in the
        current scope
      </Text>

      <TicketList>
        {tickets.map((ticket) => (
          <li key={ticket.id}>{ticket.title}</li>
        ))}
      </TicketList>

      {ineligibleCount > 0 && (
        <Alert
          type='info'
          showIcon
          message={`${ineligibleCount} ticket${ineligibleCount > 1 ? 's' : ''} in scope skipped — no NII Report step`}
        />
      )}

      {identityWarnings.length > 0 && (
        <Alert
          type='warning'
          showIcon
          message='Some tickets have no asset or site — those cells will export blank'
          description={identityWarnings.join(' · ')}
        />
      )}

      <Text type='secondary'>
        {includedSheets.length === 0
          ? 'No sheets selected — choose at least one in the panel configuration.'
          : `Sheets: ${includedSheets.map((sheet) => NII_SHEET_LABELS[sheet]).join(', ')}`}
      </Text>

      <Flex align='center' gap='small'>
        <Button
          type='primary'
          icon={<FileExcelOutlined />}
          loading={isGenerating}
          disabled={!orgId || signoffsLoading || includedSheets.length === 0}
          onClick={() => void handleExport()}>
          Download NII Spreadsheet
        </Button>
      </Flex>
    </Container>
  )
}
